import React, { useMemo } from "react";
import { Box, Typography, Button } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import ListAltIcon from "@mui/icons-material/ListAlt";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import ViewListIcon from "@mui/icons-material/ViewList";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { computeChecklistResult, totalPoints } from "./checklistScoring";

const T = {
  ink: "var(--color-ink)",
  ink2: "rgba(31,22,51,0.75)",
  ink3: "rgba(31,22,51,0.6)",
  ink4: "rgba(31,22,51,0.5)",
  line: "var(--color-hairline-cloud)",
  line2: "var(--color-hairline-cloud)",
  surface: "var(--color-surface-canvas-light)",
  surface2: "var(--color-surface-press-light)",
  surface3: "var(--color-surface-press-stronger)",
  green: "var(--color-success)",
  green50: "var(--color-success-bg)",
  amber: "var(--color-warn)",
  amber50: "var(--color-warn-bg)",
  danger: "var(--color-danger)",
  danger50: "var(--color-danger-bg)",
};

function formatRelativeTime(isoStr) {
  if (!isoStr) return "—";
  const then = new Date(isoStr).getTime();
  if (Number.isNaN(then)) return "—";
  const diffMs = Date.now() - then;
  const diffMin = Math.max(0, Math.round(diffMs / 60000));
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return new Date(isoStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function RubricTile({ icon, label, value, dim }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: "7px",
          border: `1px solid ${T.line}`,
          bgcolor: T.surface,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          opacity: dim ? 0.35 : 1,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontSize: 11, color: T.ink4, lineHeight: 1.2 }}>
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: 12.5,
            fontWeight: 700,
            color: dim ? T.ink4 : T.ink,
            lineHeight: 1.2,
          }}
        >
          {value || "—"}
        </Typography>
      </Box>
    </Box>
  );
}

function getOfficialBadge(evaluation) {
  const isLocked = evaluation?.locked;
  const isPassed = evaluation?.finalResult === "PASS";
  const isFailed = evaluation?.finalResult === "FAIL";
  if (isLocked && isPassed) {
    return {
      label: "PASSED",
      bar: T.green,
      badgeBg: T.green50,
      badgeColor: T.green,
      border: "var(--color-success-bg)",
    };
  }
  if (isLocked && isFailed) {
    return {
      label: "FAILED",
      bar: T.danger,
      badgeBg: T.danger50,
      badgeColor: T.danger,
      border: "var(--color-danger-bg)",
    };
  }
  return {
    label: "AWAITING EVAL",
    bar: T.amber,
    badgeBg: T.amber50,
    badgeColor: T.amber,
    border: "color-mix(in srgb, var(--color-warn) 20%, transparent)",
  };
}

function statusChipStyle(status) {
  if (status === "pass") {
    return { bgcolor: T.green50, color: T.green, border: "1px solid var(--color-success-bg)", label: "Pass" };
  }
  if (status === "fail") {
    return { bgcolor: T.danger50, color: T.danger, border: "1px solid var(--color-danger-bg)", label: "Fail" };
  }
  return {
    bgcolor: T.amber50,
    color: T.amber,
    border: "1px solid color-mix(in srgb, var(--color-warn) 20%, transparent)",
    label: "In progress",
  };
}

function officialFooterText(evaluation, items) {
  const isLocked = evaluation?.locked;
  const isPassed = evaluation?.finalResult === "PASS";
  const isFailed = evaluation?.finalResult === "FAIL";
  const passedCount =
    isLocked && Array.isArray(evaluation?.instructorItemResults)
      ? evaluation.instructorItemResults.filter((r) => r.pointsAwarded > 0).length
      : 0;
  if (isLocked && isPassed) return `Official: Passed · ${passedCount}/${items.length}`;
  if (isLocked && isFailed) return `Official: Failed · ${passedCount}/${items.length}`;
  return "Official: Awaiting instructor evaluation";
}

/**
 * Student-facing skill sheet card — mirrors instructor ChecklistCard density.
 * Right column = latest practice attempts (pass/fail). CTA = Practice Assessment.
 */
export default function StudentSkillCard({ checklist, evaluation, myAttempts, onPractice }) {
  const theme = useTheme();
  const tSecondary = theme.palette.secondary.main;
  const items = useMemo(() => checklist?.items || [], [checklist]);
  const itemCount = items.length;
  const maxPoints = totalPoints(items);
  const passThreshold =
    maxPoints > 0 && checklist.passingScore != null
      ? Math.round((checklist.passingScore / maxPoints) * 100)
      : null;
  const badge = getOfficialBadge(evaluation);

  const latestAttempts = useMemo(() => {
    const sorted = [...(myAttempts || [])].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
    );
    return sorted.slice(0, 3).map((attempt) => {
      const result = computeChecklistResult(
        items,
        attempt.scores || {},
        checklist.passingScore,
      );
      return { attempt, result };
    });
  }, [myAttempts, items, checklist.passingScore]);

  return (
    <Box
      sx={{
        bgcolor: T.surface,
        border: `1px solid ${T.line}`,
        borderRadius: "12px",
        mb: "14px",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          borderTop: `3px solid ${badge.bar}`,
          borderBottom: `1px solid ${T.line2}`,
          p: "14px 14px 12px 16px",
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", gap: "6px", mb: "5px", flexWrap: "wrap", alignItems: "center" }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                px: "7px",
                py: "3px",
                borderRadius: "999px",
                bgcolor: badge.badgeBg,
                border: `1px solid ${badge.border}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: badge.badgeColor,
                  textTransform: "uppercase",
                  letterSpacing: ".04em",
                  lineHeight: 1,
                }}
              >
                {badge.label}
              </Typography>
            </Box>
            {evaluation?.peerAssessmentRequired && evaluation?.peerPrereqMet && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "5px",
                  px: "7px",
                  py: "3px",
                  borderRadius: "999px",
                  bgcolor: `${tSecondary}18`,
                  border: `1px solid ${tSecondary}40`,
                }}
                title="Peer completion confirmed"
              >
                <PeopleAltIcon sx={{ fontSize: 12, color: tSecondary }} />
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: tSecondary,
                    textTransform: "uppercase",
                    letterSpacing: ".04em",
                    lineHeight: 1,
                  }}
                >
                  Peer Complete
                </Typography>
              </Box>
            )}
          </Box>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 700,
              color: T.ink,
              letterSpacing: "-.01em",
              fontFamily: "var(--font-display)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {checklist.title}
          </Typography>
          <Typography
            sx={{
              fontSize: 12.5,
              color: T.ink3,
              mt: "2px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {itemCount} skill{itemCount !== 1 ? "s" : ""}
            {checklist.description ? ` · ${checklist.description}` : ""}
          </Typography>
        </Box>
      </Box>

      {/* Body */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          borderBottom: `1px solid ${T.line2}`,
        }}
      >
        {/* RUBRIC */}
        <Box
          sx={{
            p: "14px 20px",
            borderRight: { md: `1px solid ${T.line2}` },
            borderBottom: { xs: `1px solid ${T.line2}`, md: "none" },
          }}
        >
          <Typography
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: ".1em",
              color: T.ink3,
              fontFamily: "monospace",
              mb: "10px",
            }}
          >
            RUBRIC
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 14px" }}>
            <RubricTile
              icon={<ListAltIcon sx={{ fontSize: 14, color: T.ink3 }} />}
              label="Skills"
              value={String(itemCount)}
              dim={itemCount === 0}
            />
            <RubricTile
              icon={<DoneAllIcon sx={{ fontSize: 14, color: T.ink3 }} />}
              label="Required"
              value={
                checklist.passingScore != null
                  ? `${checklist.passingScore} of ${maxPoints}`
                  : "—"
              }
              dim={checklist.passingScore == null}
            />
            <RubricTile
              icon={<StarOutlineIcon sx={{ fontSize: 14, color: T.ink3 }} />}
              label="Pass threshold"
              value={passThreshold != null ? `${passThreshold}%` : "—"}
              dim={passThreshold == null}
            />
            <RubricTile
              icon={<PersonOutlineIcon sx={{ fontSize: 14, color: T.ink3 }} />}
              label="Evaluator"
              value="Instructor"
            />
            {checklist.peerAssessmentRequired !== false && (
              <RubricTile
                icon={<PeopleAltIcon sx={{ fontSize: 14, color: T.ink3 }} />}
                label="Peer practice"
                value="Peer practice required"
              />
            )}
            <RubricTile
              icon={<ViewListIcon sx={{ fontSize: 14, color: T.ink3 }} />}
              label="Format"
              value="Pass/Fail per skill"
            />
            <RubricTile
              icon={<AccessTimeIcon sx={{ fontSize: 14, color: T.ink3 }} />}
              label="Last updated"
              value={formatRelativeTime(checklist.updatedAt)}
            />
          </Box>
        </Box>

        {/* MY PRACTICE */}
        <Box sx={{ p: "14px 20px" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: "10px",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography
              sx={{
                fontSize: 10.5,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: ".1em",
                color: T.ink3,
                fontFamily: "monospace",
              }}
            >
              MY PRACTICE
            </Typography>
            <Typography
              component="button"
              onClick={() => onPractice?.(checklist)}
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: "var(--color-primary)",
                cursor: "pointer",
                border: "none",
                bgcolor: "transparent",
                p: 0,
                fontFamily: "inherit",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              View all
            </Typography>
          </Box>

          {latestAttempts.length === 0 ? (
            <Typography sx={{ fontSize: 13, fontStyle: "italic", color: T.ink4 }}>
              No practice attempts yet.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {latestAttempts.map(({ attempt, result }) => {
                const chip = statusChipStyle(result.status);
                return (
                  <Box
                    key={attempt.id}
                    sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}
                  >
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        flexShrink: 0,
                        bgcolor: result.status === "pass" ? alpha(tSecondary, 0.1) : T.surface3,
                        border: `1px solid ${
                          result.status === "pass"
                            ? tSecondary
                            : result.status === "fail"
                              ? T.danger
                              : T.line
                        }`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color:
                          result.status === "pass"
                            ? tSecondary
                            : result.status === "fail"
                              ? T.danger
                              : T.ink3,
                      }}
                    >
                      {result.status === "pass" ? (
                        <CheckIcon sx={{ fontSize: 12 }} />
                      ) : result.status === "fail" ? (
                        <CloseIcon sx={{ fontSize: 12 }} />
                      ) : (
                        <Typography sx={{ fontSize: 10, fontWeight: 700 }}>·</Typography>
                      )}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 500,
                        flex: 1,
                        color: T.ink,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatRelativeTime(attempt.createdAt)}
                    </Typography>
                    <Box
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        px: "8px",
                        py: "2px",
                        borderRadius: "999px",
                        flexShrink: 0,
                        ...chip,
                      }}
                    >
                      {chip.label}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr auto" },
          alignItems: "center",
          p: "12px 20px",
          gap: "12px",
        }}
      >
        <Typography sx={{ fontSize: 12, color: T.ink3 }}>
          {officialFooterText(evaluation, items)}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          <Button
            size="small"
            onClick={() => onPractice?.(checklist)}
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: T.ink3,
              textTransform: "none",
              "&:hover": { bgcolor: T.surface2 },
            }}
          >
            Practice history
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<AssignmentTurnedInIcon sx={{ fontSize: "14px !important" }} />}
            onClick={() => onPractice?.(checklist)}
            sx={{
              fontSize: 12,
              fontWeight: 700,
              textTransform: "none",
              bgcolor: tSecondary,
              borderRadius: "8px",
              width: { xs: "100%", sm: "auto" },
              "&:hover": { bgcolor: "#004f47" },
            }}
          >
            Practice Assessment
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
